'use client';

import * as React from 'react';
import { ru } from '@/lib/ru';
import { isCustomBackgroundId, NONE_BACKGROUND_ID, type BackgroundOption } from '@/lib/backgrounds';
import {
  getSelectedBackgroundId,
  getSelectionEpoch,
  prewarmBackgrounds,
  useBackgroundSelection,
  useCameraBackground,
  usePrewarmBackgroundsOnIdle,
} from '@/lib/useCameraBackground';
import type { AddCustomBackgroundResult } from '@/lib/custom-backgrounds';
import styles from './BackgroundPicker.module.css';

/**
 * Miniature of a room behind a person: sharp for "no background", blurred by the
 * same amount the processor will apply.
 */
function BackgroundThumbArt(props: { blurRadius?: number }) {
  const filterId = React.useId();
  const blurred = props.blurRadius !== undefined;
  // Scaled down from the video frame to the 80x50 preview.
  const stdDeviation = blurred ? Math.max(1.5, props.blurRadius! / 4) : 0;

  return (
    <svg className={styles.art} viewBox="0 0 80 50" role="presentation" aria-hidden="true">
      {blurred && (
        <defs>
          <filter id={filterId} x="-25%" y="-25%" width="150%" height="150%">
            <feGaussianBlur stdDeviation={stdDeviation} />
          </filter>
        </defs>
      )}
      <rect width="80" height="50" fill="#ececed" />
      <g filter={blurred ? `url(#${filterId})` : undefined}>
        <rect x="0" y="34" width="80" height="16" fill="#dcd3c4" />
        <rect x="5" y="8" width="19" height="26" rx="2" fill="#8fc4a8" />
        <circle cx="66" cy="15" r="8" fill="#a9a9bb" />
        <rect x="52" y="27" width="28" height="7" rx="2" fill="#c2c2d0" />
      </g>
      <g fill="#6f6f7a">
        <circle cx="40" cy="22" r="8.5" />
        <path d="M25 50c0-8.3 6.7-15 15-15s15 6.7 15 15z" />
      </g>
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
      <path
        d="M1.5 5.2 3.8 7.5 8.5 2.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function BackgroundIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <rect
        x="1"
        y="2.5"
        width="14"
        height="11"
        rx="2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
      />
      <circle cx="8" cy="7" r="2.1" fill="currentColor" />
      <path d="M4 13c0-2.2 1.8-4 4-4s4 1.8 4 4" fill="currentColor" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
      <path
        d="M2.5 2.5l7 7m0-7-7 7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Centered on the upload tile, over the same miniature as "no background". */
function UploadPlusIcon() {
  return (
    <svg
      className={styles.uploadPlus}
      width="20"
      height="20"
      viewBox="0 0 20 20"
      aria-hidden="true"
    >
      <circle cx="10" cy="10" r="9" className={styles.uploadPlusCircle} />
      <path
        d="M10 5.8v8.4M5.8 10h8.4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function uploadErrorText(reason: Exclude<AddCustomBackgroundResult, { ok: true }>['reason']) {
  switch (reason) {
    case 'not-image':
      return ru.backgroundUploadInvalid;
    case 'too-large':
      return ru.backgroundUploadTooLarge;
    case 'too-large-resolution':
      return ru.backgroundUploadTooLargeResolution;
    case 'too-many':
      return ru.backgroundStorageFull;
    default:
      return ru.backgroundUploadFailed;
  }
}

function BackgroundTile(props: {
  option: BackgroundOption;
  isActive: boolean;
  isPending: boolean;
  onSelect: (id: string) => void;
}) {
  const { option, isActive, isPending, onSelect } = props;

  return (
    <button
      type="button"
      className={`${styles.tile} ${isActive ? styles.tileActive : ''}`}
      aria-pressed={isActive}
      title={option.label}
      onClick={() => onSelect(option.id)}
    >
      <span
        className={styles.thumb}
        style={option.kind === 'image' ? { backgroundImage: `url(${option.thumb})` } : undefined}
      >
        {option.kind === 'none' && <BackgroundThumbArt />}
        {option.kind === 'blur' && <BackgroundThumbArt blurRadius={option.blurRadius} />}
        {isActive && isPending && (
          <span className={styles.spinnerOverlay}>
            <span className={styles.spinner} />
          </span>
        )}
        {isActive && !isPending && (
          <span className={styles.badge}>
            <CheckIcon />
          </span>
        )}
      </span>
      <span className={styles.label}>{option.label}</span>
    </button>
  );
}

/**
 * Grid of camera background options. Works with or without a room, so the same
 * picker is used in the pre-join card, in the control bar and in the settings
 * menu. `onClose` adds a close button — used when shown as a popover.
 */
export function BackgroundPicker(props: {
  onClose?: () => void;
  showTitle?: boolean;
  compact?: boolean;
  hasCamera?: boolean;
}) {
  const { options, activeId, status, select, addCustom, removeCustom } = useBackgroundSelection();
  usePrewarmBackgroundsOnIdle();
  const cameraOff = props.hasCamera === false;
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [uploadPending, setUploadPending] = React.useState(false);
  const [uploadNote, setUploadNote] = React.useState<
    { text: string; isError: boolean } | undefined
  >();

  const handleUpload = async (file: File) => {
    setUploadNote(undefined);
    setUploadPending(true);
    // Processing can take a while; if the user picks another background in the
    // meantime, that choice wins over the upload's automatic one.
    const selectionEpoch = getSelectionEpoch();
    try {
      const result = await addCustom(file);
      if (result.ok) {
        if (getSelectionEpoch() === selectionEpoch) {
          select(result.id);
        }
        // Quota or private mode: the background works, it just won't survive a
        // reload — say so instead of failing the pick.
        setUploadNote(
          result.persisted ? undefined : { text: ru.backgroundSaveFailed, isError: true },
        );
      } else {
        setUploadNote({ text: uploadErrorText(result.reason), isError: true });
      }
    } finally {
      setUploadPending(false);
    }
  };

  const handleFileChosen = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Clear the input so choosing the same file again still fires onChange.
    event.target.value = '';
    if (file) {
      void handleUpload(file);
    }
  };

  // Any settled action retires the note from the previous attempt.
  const handleSelect = (id: string) => {
    setUploadNote(undefined);
    select(id);
  };

  const handleRemove = async (option: BackgroundOption) => {
    const removed = await removeCustom(option.id);
    if (!removed) {
      setUploadNote({ text: ru.backgroundRemoveFailed, isError: true });
      return;
    }
    setUploadNote(undefined);
    // The selection may have moved on while the removal was in flight; fall
    // back to "none" only if the removed background is still the active one.
    if (getSelectedBackgroundId() === option.id) {
      select(NONE_BACKGROUND_ID);
    }
  };

  const note =
    status === 'unsupported'
      ? { text: ru.backgroundUnsupported, isError: true }
      : status === 'error'
        ? { text: ru.backgroundFailed, isError: true }
        : status === 'pending'
          ? { text: ru.backgroundApplying, isError: false }
          : (uploadNote ??
            (cameraOff ? { text: ru.backgroundCameraOff, isError: false } : undefined));

  return (
    // Hovering the picker is a good sign the user is about to change the
    // background: start loading the segmentation assets now.
    <div
      className={`${styles.root} ${props.compact ? styles.compact : ''}`}
      data-background-status={status}
      onPointerEnter={prewarmBackgrounds}
      onFocus={prewarmBackgrounds}
    >
      {(props.onClose || props.showTitle) && (
        <div className={styles.header}>
          <h2 className={styles.title}>{ru.backgroundEffects}</h2>
          {props.onClose && (
            <button
              type="button"
              className={styles.close}
              onClick={props.onClose}
              aria-label={ru.close}
            >
              <CloseIcon />
            </button>
          )}
        </div>
      )}
      <div className={styles.grid}>
        {options.map((option) =>
          isCustomBackgroundId(option.id) ? (
            // A wrapper instead of nesting buttons: the tile and its delete
            // button are separate controls.
            <div key={option.id} className={styles.tileWrap}>
              <BackgroundTile
                option={option}
                isActive={option.id === activeId}
                isPending={status === 'pending'}
                onSelect={handleSelect}
              />
              <button
                type="button"
                className={styles.remove}
                title={ru.backgroundRemove}
                aria-label={ru.backgroundRemove}
                onClick={() => void handleRemove(option)}
              >
                <CloseIcon />
              </button>
            </div>
          ) : (
            <BackgroundTile
              key={option.id}
              option={option}
              isActive={option.id === activeId}
              isPending={status === 'pending'}
              onSelect={handleSelect}
            />
          ),
        )}
        <button
          type="button"
          className={`${styles.tile} ${styles.tileUpload}`}
          title={ru.backgroundUpload}
          disabled={uploadPending}
          onClick={() => fileInputRef.current?.click()}
        >
          <span className={styles.thumb}>
            <BackgroundThumbArt />
            <UploadPlusIcon />
          </span>
          <span className={styles.label}>{ru.backgroundUpload}</span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className={styles.fileInput}
          onChange={handleFileChosen}
        />
      </div>
      {note && (
        <p className={`${styles.note} ${note.isError ? styles.noteError : ''}`}>{note.text}</p>
      )}
    </div>
  );
}

/** Control-bar button that opens the background picker in a popover. */
export function BackgroundPickerToggle(props: { showText?: boolean }) {
  // Mounted for the whole call: this is what keeps the camera track in sync with
  // the selection, including the one restored from a previous meeting.
  const { activeId, hasCamera } = useCameraBackground();
  usePrewarmBackgroundsOnIdle();
  const [isOpen, setIsOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      if (!anchorRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  const isBackgroundOn = activeId !== NONE_BACKGROUND_ID;

  return (
    <div className={styles.anchor} ref={anchorRef}>
      <button
        type="button"
        className={`lk-button ${styles.toggle} ${isBackgroundOn ? styles.toggleActive : ''}`}
        aria-pressed={isOpen}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        title={ru.backgroundEffects}
        onPointerEnter={prewarmBackgrounds}
        onFocus={prewarmBackgrounds}
        onClick={() => setIsOpen((open) => !open)}
      >
        <BackgroundIcon />
        {props.showText && ru.backgroundEffects}
      </button>
      {isOpen && (
        <div className={styles.popover} role="dialog" aria-label={ru.backgroundEffects}>
          <BackgroundPicker onClose={() => setIsOpen(false)} hasCamera={hasCamera} />
        </div>
      )}
    </div>
  );
}
